import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Clock,
  Loader2,
  Package,
  Pencil,
  Plus,
  Save,
  Trash2,
} from "lucide-react";
import { EntityTableTab } from "@/components/shared/EntityTableTab";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { ReferenceIdBadge } from "@/components/ui/ReferenceIdBadge";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { AppLayout } from "@/components/layout/AppLayout";
import { ActivityHistoryDrawer } from "@/components/audit/ActivityHistoryDrawer";
import { TimelineTab } from "@/components/shared/TimelineTab";
import { formatDateTime } from "@/lib/utils";
import {
  useProduct,
  useProductSubEntity,
  useDeleteProduct,
  useUpdateProduct,
} from "../hooks/useProducts";
import { NewProjectDrawer } from "@/modules/projects/components/NewProjectDrawer";
import { NewTaskDrawer } from "@/modules/tasks/components/NewTaskDrawer";
import { NewActionDrawer } from "@/modules/actions/components/NewActionDrawer";
import { NewMeetingDrawer } from "@/modules/meetings/components/NewMeetingDrawer";
import { NewMilestoneDrawer } from "@/modules/milestones/components/NewMilestoneDrawer";
import { NotesTab } from "@/modules/notes/components/NotesTab";
import { BudgetsTabContent } from "@/modules/budgets/components/BudgetsTabContent";
import { ExpensesTabContent } from "@/modules/expenses/components/ExpensesTabContent";
import { EntityDocumentsTab } from "@/modules/media/components/EntityDocumentsTab";
import { ProductModulesTab } from "../components/ProductModulesTab";
import { EntityTeamTab } from "@/modules/teams/components/EntityTeamTab";
import { useTeam } from "@/modules/teams/hooks/useTeam";
import type { ProductDetail, ProductStatus } from "../types";

type Tab =
  | "overview"
  | "projects"
  | "meetings"
  | "tasks"
  | "actions"
  | "notes"
  | "milestones"
  | "decisions"
  | "budgets"
  | "expenses"
  | "team"
  | "documents"
  | "modules"
  | "timeline";

const TABS: { key: Tab; label: string; counted?: boolean }[] = [
  { key: "overview", label: "Overview" },
  { key: "projects", label: "Projects" },
  { key: "meetings", label: "Meetings", counted: true },
  { key: "tasks", label: "Tasks", counted: true },
  { key: "actions", label: "Actions", counted: true },
  { key: "notes", label: "Notes", counted: true },
  { key: "milestones", label: "Milestones" },
  { key: "decisions", label: "Decisions" },
  { key: "budgets", label: "Budgets" },
  { key: "expenses", label: "Expenses" },
  { key: "team", label: "Team", counted: true },
  { key: "documents", label: "Documents" },
  { key: "modules", label: "Modules" },
  { key: "timeline", label: "Timeline" },
];

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; fg: string; label: string }> = {
    ACTIVE: { bg: "hsl(142,55%,92%)", fg: "hsl(142,72%,28%)", label: "Active" },
    INACTIVE: { bg: "hsl(210,16%,93%)", fg: "hsl(215,16%,40%)", label: "Inactive" },
    ARCHIVED: { bg: "hsl(38,92%,92%)", fg: "hsl(38,92%,30%)", label: "Archived" },
  };
  const s = styles[status] ?? styles.INACTIVE;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={{ background: s.bg, color: s.fg }}
    >
      {s.label}
    </span>
  );
}

function FieldRow({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div className="space-y-1">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="text-sm text-foreground">{value || "—"}</div>
    </div>
  );
}

export default function ProductDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: product, isLoading, isError } = useProduct(id);
  const deleteProduct = useDeleteProduct();
  const updateProduct = useUpdateProduct(id);

  const [tab, setTab] = useState<Tab>("overview");
  const [editMode, setEditMode] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [projectDrawerOpen, setProjectDrawerOpen] = useState(false);
  const [taskDrawerOpen, setTaskDrawerOpen] = useState(false);
  const [actionDrawerOpen, setActionDrawerOpen] = useState(false);
  const [meetingDrawerOpen, setMeetingDrawerOpen] = useState(false);
  const [milestoneDrawerOpen, setMilestoneDrawerOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // Sub-entity queries (only fetched when tab is active)
  const projects = useProductSubEntity<any>(id, "projects", tab === "projects" ? { page: 1, limit: 50 } : {});
  const meetings = useProductSubEntity<any>(id, "meetings", tab === "meetings" ? { page: 1, limit: 50 } : {});
  const tasks = useProductSubEntity<any>(id, "tasks", tab === "tasks" ? { page: 1, limit: 50 } : {});
  const actions = useProductSubEntity<any>(id, "actions", tab === "actions" ? { page: 1, limit: 50 } : {});
  const milestones = useProductSubEntity<any>(id, "milestones", tab === "milestones" ? { page: 1, limit: 50 } : {});
  const decisions = useProductSubEntity<any>(id, "decisions", tab === "decisions" ? { page: 1, limit: 50 } : {});
  const teamQuery = useTeam("PRODUCT", id);
  const teamCount = teamQuery.data?.length ?? 0;

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex min-h-[60vh] items-center justify-center">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
          <span className="text-sm text-muted-foreground">
            Loading product…
          </span>
        </div>
      </AppLayout>
    );
  }

  if (isError || !product) {
    return (
      <AppLayout>
        <div className="flex min-h-[60vh] items-center justify-center text-sm text-destructive">
          Failed to load product.
        </div>
      </AppLayout>
    );
  }

  function handleDelete() {
    setDeleteOpen(true);
  }

  async function confirmDelete() {
    await deleteProduct.mutateAsync(id);
    navigate("/products");
  }

  const tabCount = (key: Tab): string | undefined => {
    if (key === "meetings") return String(product._count.meetings);
    if (key === "tasks") return String(product._count.tasks);
    if (key === "actions") return String(product._count.actions);
    if (key === "notes") return "0";
    if (key === "team") return String(teamCount);
    return undefined;
  };

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Business Structure" },
        { label: "Products", href: "/products" },
        { label: product.name },
      ]}
    >
      <div className="w-full space-y-6">
        {/* Toast */}
        {toast && (
          <div className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium text-green-700 shadow-lg" style={{ borderColor: "hsl(142,55%,80%)" }}>
            {toast}
          </div>
        )}

        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/products")}
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            </Button>
            <Package className="h-[18px] w-[18px]" strokeWidth={1.5} />
            <h1 className="text-2xl font-semibold tracking-tight">
              View Product
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              aria-label="History"
              onClick={() => setHistoryOpen(true)}
            >
              <Clock className="h-4 w-4" strokeWidth={1.5} />
            </Button>
            <Button
              variant="outline"
              size="icon"
              aria-label="Edit"
              onClick={() => {
                setEditMode((v) => !v);
                if (editMode) setTab("overview");
              }}
            >
              <Pencil className="h-4 w-4" strokeWidth={1.5} />
            </Button>
            <Button
              variant="outline"
              size="icon"
              aria-label="Delete"
              onClick={handleDelete}
            >
              <Trash2 className="h-4 w-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>

        {/* Product header card */}
        <Card className="w-full">
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <h2
                  className="font-bold tracking-tight"
                  style={{ fontSize: 20, fontWeight: 700 }}
                >
                  {product.name}
                </h2>
                <div className="mt-1.5 flex flex-wrap items-center gap-3">
                  <ReferenceIdBadge id={product.code} />
                  <StatusBadge status={product.status} />
                  <a
                    href={`/companies/${product.company.id}`}
                    className="flex items-center gap-1 text-xs text-primary hover:underline"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/companies/${product.company.id}`);
                    }}
                  >
                    <Building2 className="h-3 w-3" strokeWidth={1.5} />
                    {product.company.name}
                  </a>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tab bar */}
        <div className="border-b border-border">
          <nav className="-mb-px flex gap-4 overflow-x-auto">
            {TABS.map((t) => {
              const count = tabCount(t.key);
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`whitespace-nowrap border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                    tab === t.key
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t.label}
                  {t.counted && count && (
                    <span className="ml-1 text-[10px] text-muted-foreground">
                      ({count})
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab content */}
        {tab === "overview" && (
          <OverviewTab
            product={product}
            editMode={editMode}
            onSave={async (payload) => {
              await updateProduct.mutateAsync(payload);
              setEditMode(false);
              showToast("Product updated successfully");
            }}
            onCancel={() => setEditMode(false)}
            isSaving={updateProduct.isPending}
          />
        )}
        {tab === "projects" && (
          <div className="space-y-3">
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setProjectDrawerOpen(true)}>
                <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
                New Project
              </Button>
            </div>
            <EntityTableTab
              data={projects.data?.data ?? []}
              isLoading={projects.isLoading && !projects.data}
              columns={["name", "code", "type", "status", "progress", "startDate", "endDate"]}
              emptyText="No projects found"
              onRowClick={(row) => navigate(`/projects/${row.id}`)}
            />
          </div>
        )}
        {tab === "meetings" && (
          <div className="space-y-3">
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setMeetingDrawerOpen(true)}>
                <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
                New Meeting
              </Button>
            </div>
            <EntityTableTab
              data={meetings.data?.data ?? []}
              isLoading={meetings.isLoading && !meetings.data}
              columns={["title", "status", "scheduledAt", "organizer", "entityContext"]}
              emptyText="No meetings found"
              onRowClick={(row) => navigate(`/meetings/${row.id}`)}
            />
          </div>
        )}
        {tab === "tasks" && (
          <div className="space-y-3">
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setTaskDrawerOpen(true)}>
                <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
                New Task
              </Button>
            </div>
            <EntityTableTab
              data={tasks.data?.data ?? []}
              isLoading={tasks.isLoading && !tasks.data}
              columns={["title", "priority", "projectName", "status", "dueDate", "assignee"]}
              emptyText="No tasks found"
              onRowClick={(row) => navigate(`/tasks/${row.id}`)}
            />
          </div>
        )}
        {tab === "actions" && (
          <div className="space-y-3">
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setActionDrawerOpen(true)}>
                <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
                New Action
              </Button>
            </div>
            <EntityTableTab
              data={actions.data?.data ?? []}
              isLoading={actions.isLoading && !actions.data}
              columns={["title", "priority", "source", "projectName", "status", "dueDate", "assignee"]}
              emptyText="No actions found"
              onRowClick={(row) => navigate(`/actions/${row.id}`)}
            />
          </div>
        )}
        {tab === "notes" && (
          <NotesTab
            filter={{ productId: product.id }}
            prefill={{
              productId: product.id,
              companyId: product.company.id,
              locked: { productId: true, companyId: true },
            }}
          />
        )}
        {tab === "milestones" && (
          <div className="space-y-3">
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setMilestoneDrawerOpen(true)}>
                <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
                New Milestone
              </Button>
            </div>
            <EntityTableTab
              data={milestones.data?.data ?? []}
              isLoading={milestones.isLoading && !milestones.data}
              columns={["title", "status", "progress", "dueDate", "entityContext"]}
              emptyText="No milestones found"
              onRowClick={(row) => navigate(`/milestones/${row.id}`)}
            />
          </div>
        )}
        {tab === "decisions" && (
          <EntityTableTab
            data={decisions.data?.data ?? []}
            isLoading={decisions.isLoading && !decisions.data}
            columns={["title", "status", "decidedBy", "decidedAt", "entityContext"]}
            emptyText="No decisions found"
            onRowClick={(row) => navigate(`/decisions/${row.id}`)}
          />
        )}
        {tab === "budgets" && <BudgetsTabContent productId={product.id} />}
        {tab === "expenses" && (
          <ExpensesTabContent
            productId={product.id}
            productIncludeProjectLinks
          />
        )}
        {tab === "team" && (
          <EntityTeamTab entityType="PRODUCT" entityId={product.id} />
        )}
        {tab === "documents" && (
          <EntityDocumentsTab
            entityType="PRODUCT"
            entityId={product.id}
            entityLabel={product.name}
            defaultView="tree"
          />
        )}
        {tab === "modules" && <ProductModulesTab productId={id} />}
        {tab === "timeline" && <TimelineTab entityType="product" entityId={id} />}

        {/* Drawers */}
        <ActivityHistoryDrawer
          isOpen={historyOpen}
          onClose={() => setHistoryOpen(false)}
          entityType="product"
          entityId={product.id}
          entityLabel={product.name}
        />
        <NewProjectDrawer
          isOpen={projectDrawerOpen}
          onClose={() => setProjectDrawerOpen(false)}
          productId={id}
          companyId={product.company.id}
        />
        <NewTaskDrawer
          isOpen={taskDrawerOpen}
          onClose={() => setTaskDrawerOpen(false)}
          prefill={{
            productId: product.id,
            companyId: product.company.id,
            locked: { productId: true },
          }}
          onCreated={() => setToast("Task created")}
        />
        <NewActionDrawer
          isOpen={actionDrawerOpen}
          onClose={() => setActionDrawerOpen(false)}
          prefill={{
            productId: product.id,
            companyId: product.company.id,
            locked: { productId: true, companyId: true },
          }}
          onCreated={() => setToast("Action created")}
        />
        <NewMeetingDrawer
          isOpen={meetingDrawerOpen}
          onClose={() => setMeetingDrawerOpen(false)}
          onCreated={(code) => showToast(`Meeting created: ${code}`)}
        />
        <NewMilestoneDrawer
          isOpen={milestoneDrawerOpen}
          onClose={() => setMilestoneDrawerOpen(false)}
          prefill={{
            productId: product.id,
            companyId: product.company.id,
            locked: { productId: true, companyId: true },
          }}
          onCreated={() => setToast("Milestone created")}
        />

        <ConfirmDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          title="Delete product?"
          description={`Are you sure you want to delete '${product.name}'? It will be removed from your Products list.`}
          onConfirm={confirmDelete}
        />
      </div>
    </AppLayout>
  );
}

// ── Overview Tab with Edit Mode ──────────────────────────
type EditFormState = {
  name: string;
  status: ProductStatus;
  description: string;
};

function initForm(p: ProductDetail): EditFormState {
  return {
    name: p.name,
    status: p.status,
    description: p.description ?? "",
  };
}

function OverviewTab({
  product,
  editMode,
  onSave,
  onCancel,
  isSaving,
}: {
  product: ProductDetail;
  editMode: boolean;
  onSave: (payload: Record<string, any>) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
}) {
  const navigate = useNavigate();
  const [form, setForm] = useState<EditFormState>(() => initForm(product));

  // Reset form when product data changes or edit mode toggles
  useEffect(() => {
    setForm(initForm(product));
  }, [product, editMode]);

  function set<K extends keyof EditFormState>(field: K, value: EditFormState[K]) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSave() {
    if (!form.name.trim()) return;
    await onSave({
      name: form.name.trim(),
      status: form.status,
      description: form.description.trim() || null,
    });
  }

  return (
    <div className="grid w-full grid-cols-1 gap-6 lg:grid-cols-10">
      {/* Left 70% */}
      <div className="space-y-6 lg:col-span-7">
        <Card>
          <CardContent className="space-y-5 pt-6">
            <h3 className="text-sm font-semibold text-foreground">
              Basic Information
            </h3>
            {editMode ? (
              <>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-name">
                      Product Name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="edit-name"
                      value={form.name}
                      onChange={(e) => set("name", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Product Code</Label>
                    <Input value={product.code} disabled className="bg-muted font-mono" />
                    <p className="text-[11px] text-muted-foreground">
                      Code is auto-generated and immutable.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-status">Status</Label>
                    <Select
                      id="edit-status"
                      value={form.status}
                      onChange={(e) => set("status", e.target.value as ProductStatus)}
                    >
                      <option value="ACTIVE">Active</option>
                      <option value="INACTIVE">Inactive</option>
                      <option value="ARCHIVED">Archived</option>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Company</Label>
                    <Input value={product.company.name} disabled className="bg-muted" />
                    <p className="text-[11px] text-muted-foreground">
                      Company cannot be changed.
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-description">Description</Label>
                  <textarea
                    id="edit-description"
                    value={form.description}
                    onChange={(e) => set("description", e.target.value)}
                    rows={4}
                    className="flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder="Product description"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <FieldRow label="Product Name" value={product.name} />
                  <FieldRow label="Product Code" value={product.code} />
                  <div className="space-y-1">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      Status
                    </div>
                    <StatusBadge status={product.status} />
                  </div>
                  <div className="space-y-1">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      Company
                    </div>
                    <a
                      href={`/companies/${product.company.id}`}
                      className="text-sm font-medium text-primary hover:underline"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/companies/${product.company.id}`);
                      }}
                    >
                      {product.company.name}
                    </a>
                  </div>
                </div>
                <FieldRow label="Description" value={product.description} />
              </>
            )}
          </CardContent>
        </Card>

        {/* Save/Cancel buttons (only in edit mode) */}
        {editMode && (
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving || !form.name.trim()}>
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
              ) : (
                <Save className="h-4 w-4" strokeWidth={1.5} />
              )}
              Save Changes
            </Button>
          </div>
        )}
      </div>

      {/* Right 30% */}
      <div className="space-y-6 lg:col-span-3">
        <Card>
          <CardContent className="space-y-3 pt-6">
            <h3 className="text-sm font-semibold text-foreground">
              Parent Company
            </h3>
            <a
              href={`/companies/${product.company.id}`}
              className="text-sm font-medium text-primary hover:underline"
              onClick={(e) => {
                e.preventDefault();
                navigate(`/companies/${product.company.id}`);
              }}
            >
              {product.company.name}
            </a>
            <div className="font-mono text-xs text-muted-foreground">
              {product.company.code}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-4 pt-6">
            <h3 className="text-sm font-semibold text-foreground">Dates</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <Clock
                  className="mt-0.5 h-4 w-4 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Created At
                  </div>
                  <div className="text-foreground">
                    {formatDateTime(product.createdAt)}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Clock
                  className="mt-0.5 h-4 w-4 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Updated At
                  </div>
                  <div className="text-foreground">
                    {formatDateTime(product.updatedAt)}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

